/**
 * Track 4 — Quantity Intelligence page.
 *
 * Project-level rollup of placed assets: by type, by status, by sheet.
 * Plus simple CSV / JSON export. No PDF system, no AI takeoff.
 */
import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { Hammer, Download, Loader2, FileSpreadsheet, FileJson, Ruler, AlertTriangle } from "lucide-react";
import { api } from "@/lib/api";


export default function QuantityIntelligence() {
  const [params] = useSearchParams();
  const projectId = params.get("project_id") || "";
  const [intel, setIntel] = useState(null);
  const [error, setError] = useState(null);
  const [traceable, setTraceable] = useState(null);
  const [traceableError, setTraceableError] = useState(null);

  useEffect(() => {
    if (!projectId) return;
    let alive = true;
    api.get(`/projects/${projectId}/assets/intelligence`)
      .then((r) => alive && setIntel(r.data))
      .catch((e) => alive && setError(e?.response?.data?.detail || "Couldn't load intelligence"));
    api.get(`/projects/${projectId}/measurements/traceable`)
      .then((r) => alive && setTraceable(r.data))
      .catch((e) => alive && setTraceableError(e?.response?.data?.detail || "Couldn't load traceable quantities"));
    return () => { alive = false; };
  }, [projectId]);

  const downloadTraceableCsv = async () => {
    const res = await api.get(`/projects/${projectId}/measurements/traceable/export?format=csv`, { responseType: "blob" });
    const url = window.URL.createObjectURL(new Blob([res.data]));
    const a = document.createElement("a");
    a.href = url; a.download = `traceable-quantities-${projectId}.csv`;
    document.body.appendChild(a); a.click(); a.remove();
    window.URL.revokeObjectURL(url);
  };

  const downloadCsv = async () => {
    const res = await api.get(`/projects/${projectId}/assets/export?format=csv`, { responseType: "blob" });
    const url = window.URL.createObjectURL(new Blob([res.data]));
    const a = document.createElement("a");
    a.href = url; a.download = `assets-${projectId}.csv`;
    document.body.appendChild(a); a.click(); a.remove();
    window.URL.revokeObjectURL(url);
  };
  const downloadJson = async () => {
    const res = await api.get(`/projects/${projectId}/assets/export?format=json`);
    const blob = new Blob([JSON.stringify(res.data, null, 2)], { type: "application/json" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `assets-${projectId}.json`;
    document.body.appendChild(a); a.click(); a.remove();
    window.URL.revokeObjectURL(url);
  };

  if (!projectId) {
    return <div className="p-8 text-sm text-[var(--fop-text-3)]">Select a project to view its quantity intelligence.</div>;
  }
  if (error) return <div className="p-8 text-sm text-[var(--fop-text-3)]">{error}</div>;
  if (!intel) return (
    <div className="p-8 text-sm text-[var(--fop-text-3)] flex items-center gap-2" data-testid="quantity-intelligence-loading">
      <Loader2 className="animate-spin" size={14} /> Counting placed assets…
    </div>
  );

  return (
    <div className="p-4 max-w-5xl mx-auto" data-testid="quantity-intelligence">
      <header className="mb-4">
        <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-[var(--fop-primary)]">Quantity intelligence</div>
        <h1 className="text-xl font-semibold text-[var(--fop-text)] flex items-center gap-2">
          <Hammer size={18} /> {intel.total} {intel.total === 1 ? "asset" : "assets"} placed
        </h1>
        <div className="mt-2 flex gap-2">
          <button
            type="button" onClick={downloadCsv}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded border border-[var(--fop-border)] text-[12px] text-[var(--fop-text)] hover:bg-[var(--fop-surface-2)]"
            data-testid="quantity-intelligence-export-csv"
          ><FileSpreadsheet size={12} /> Export CSV</button>
          <button
            type="button" onClick={downloadJson}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded border border-[var(--fop-border)] text-[12px] text-[var(--fop-text)] hover:bg-[var(--fop-surface-2)]"
            data-testid="quantity-intelligence-export-json"
          ><FileJson size={12} /> Export JSON</button>
        </div>
      </header>

      {intel.total === 0 ? (
        <div className="p-8 text-center text-[var(--fop-text-3)] border border-dashed border-[var(--fop-border)] rounded">
          <Hammer size={28} className="mx-auto opacity-60" />
          <div className="mt-2 text-sm">No assets placed yet on this project.</div>
          <div className="text-[12px] mt-1">Open any sheet, tap the Asset tool, and place a manhole / inlet / valve / sign.</div>
        </div>
      ) : (
        <div className="grid md:grid-cols-3 gap-4">
          <Card title="By Type" rows={intel.by_type} keyField="asset_type" labelField="label" testid="qi-by-type" />
          <Card title="By Status" rows={intel.by_status} keyField="status" labelField="label" testid="qi-by-status" />
          <Card
            title="By Sheet"
            rows={intel.by_sheet.map((s) => ({ key: s.sheet_id,
              label: s.sheet_number || s.sheet_id,
              count: s.count, link: `/sheet/${s.sheet_id}` }))}
            keyField="key" labelField="label"
            testid="qi-by-sheet"
          />
        </div>
      )}

      {/* R12 (2026-09-10) — Traceable quantities. Every measurement's
          value on its own is not a traceable quantity; a recipient must
          be able to walk back from it to sheet/revision, calibration,
          units, and trust. This section surfaces exactly the same field
          set the CSV export returns, so the two can never drift apart. */}
      <section className="mt-6 rounded border border-[var(--fop-border)] bg-[var(--fop-surface)] overflow-hidden" data-testid="traceable-quantities-section">
        <header className="px-3 py-1.5 border-b border-[var(--fop-border)] font-mono text-[10px] uppercase tracking-[0.22em] text-[var(--fop-text-2)] bg-[var(--fop-surface-2)] flex items-center justify-between">
          <span className="flex items-center gap-1.5"><Ruler size={11} /> Traceable quantities (measurements)</span>
          {traceable && traceable.total > 0 && (
            <button
              type="button" onClick={downloadTraceableCsv}
              className="inline-flex items-center gap-1 text-[11px] text-[var(--fop-text)] hover:text-[var(--fop-primary)] normal-case tracking-normal font-sans"
              data-testid="traceable-quantities-export-csv"
            ><FileSpreadsheet size={11} /> Export CSV</button>
          )}
        </header>
        {traceableError ? (
          <div className="p-3 text-[12px] text-[var(--fop-text-3)]">{traceableError}</div>
        ) : !traceable ? (
          <div className="p-3 text-[12px] text-[var(--fop-text-3)] flex items-center gap-2">
            <Loader2 className="animate-spin" size={12} /> Loading traceable quantities…
          </div>
        ) : traceable.total === 0 ? (
          <div className="p-3 text-[12px] text-[var(--fop-text-3)]">No measurements taken on this project yet.</div>
        ) : (
          <div>
            <div className="px-3 py-2 flex items-center gap-4 text-[12px] border-b border-[var(--fop-border)]" data-testid="traceable-quantities-summary">
              <span className="text-[var(--fop-text)]"><b className="tabular-nums">{traceable.total}</b> measurement{traceable.total === 1 ? "" : "s"}</span>
              {traceable.stale_count > 0 ? (
                <span className="flex items-center gap-1 text-amber-500" data-testid="traceable-quantities-stale-count">
                  <AlertTriangle size={12} /> <b className="tabular-nums">{traceable.stale_count}</b> stale or unresolved
                </span>
              ) : (
                <span className="text-[var(--fop-text-3)]">All quantities current and trusted</span>
              )}
            </div>
            <ul className="max-h-72 overflow-y-auto">
              {traceable.measurements.slice(0, 50).map((m) => (
                <li key={m.measurement_id}
                    data-testid={`traceable-quantity-row-${m.measurement_id}`}
                    className="px-3 py-2 border-b border-[var(--fop-border)] last:border-b-0 flex items-center gap-2 text-[12px]">
                  <span className="font-mono tabular-nums w-20 text-right text-[var(--fop-text)]">{m.value != null ? `${Number(m.value).toLocaleString(undefined, { maximumFractionDigits: 2 })} ${m.unit || ""}` : "—"}</span>
                  <Link to={`/sheet/${m.sheet_id}`} className="text-[var(--fop-text)] hover:text-[var(--fop-primary)] truncate flex-1">
                    {m.label || m.kind} · {m.sheet_number} REV {m.revision}
                  </Link>
                  {m.stale && (
                    <span className="text-amber-500 text-[10px] font-mono uppercase" data-testid={`traceable-quantity-stale-badge-${m.measurement_id}`}>
                      {m.requires_remeasurement ? "re-measure" : !m.is_current_revision ? "not current rev" : m.scale_trust || "unverified"}
                    </span>
                  )}
                </li>
              ))}
            </ul>
          </div>
        )}
      </section>
    </div>
  );
}


function Card({ title, rows, keyField, labelField, testid }) {
  return (
    <section className="rounded border border-[var(--fop-border)] bg-[var(--fop-surface)] overflow-hidden" data-testid={testid}>
      <header className="px-3 py-1.5 border-b border-[var(--fop-border)] font-mono text-[10px] uppercase tracking-[0.22em] text-[var(--fop-text-2)] bg-[var(--fop-surface-2)]">
        {title}
      </header>
      {rows.length === 0 ? (
        <div className="p-3 text-[12px] text-[var(--fop-text-3)]">—</div>
      ) : (
        <ul>
          {rows.map((r) => (
            <li key={r[keyField] ?? r.key ?? r.label}
                data-testid={`${testid}-row-${r[keyField] ?? r.key ?? r.label}`}
                className="px-3 py-2 border-b border-[var(--fop-border)] last:border-b-0 flex items-center gap-2">
              <span className="font-mono text-base tabular-nums text-[var(--fop-text)] w-10 text-right">{r.count}</span>
              {r.link ? (
                <Link to={r.link} className="flex-1 text-[13px] text-[var(--fop-text)] hover:text-[var(--fop-primary)]">{r[labelField]}</Link>
              ) : (
                <span className="flex-1 text-[13px] text-[var(--fop-text)]">{r[labelField]}</span>
              )}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
